import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import s from "./Testing.module.css";
import { useConsumption, useLatestTest } from "../../api/queries";
import {
  compareWindows,
  OUTCOME_LABEL,
  proposedThresholds,
  totalSeries,
  type ComparisonRow,
  type Outcome,
} from "../../domain/testing";
import { Card, EmptyState, ErrorState, numClass, Segmented, Skeleton, Table } from "../../design/components";
import { formatDate } from "../../lib/format";
import { ChangeChart, DistributionChart, TrendChart, type Level } from "./charts";
import { locale, t } from "../../i18n";

const fmt = (v: number | undefined, d = 1) =>
  v == null ? "—" : v.toLocaleString(locale(), { maximumFractionDigits: d });
const label = (v: unknown) => {
  const text = String(v ?? "")
    .replace(/[_-]+/g, " ")
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : t("Not recorded");
};

export function OutcomeText({ outcome }: { outcome: Outcome }) {
  return <b className={s[outcome]}>{t(OUTCOME_LABEL[outcome])}</b>;
}

/**
 * The latest test of one type on this equipment, against the test before:
 * the verdict, how the values moved, the spread against the thresholds, the
 * worst assets, and a breakdown by an attribute (colour, circuit).
 */
export function TestComparisonCard({
  testType,
  title,
  unit,
  groupBy,
  reporting,
}: {
  testType: string;
  title: string;
  unit: string;
  groupBy: { key: string; label: string }[];
  reporting: boolean;
}) {
  const q = useLatestTest(testType);
  const [group, setGroup] = useState(groupBy[0]?.key ?? "");

  const data = q.data;
  const rows = useMemo<ComparisonRow[]>(
    () =>
      data?.comparison?.rows ??
      data?.results.map((r) => ({
        key: r.assetTag ?? r.location ?? String(r.id),
        assetTag: r.assetTag,
        location: r.location,
        attrs: r.attrs,
        latest: r.judgedValue ?? r.value,
        latestOutcome: r.outcome,
      })) ??
      [],
    [data],
  );

  if (q.isPending)
    return (
      <Card title={title}>
        <Skeleton rows={4} />
      </Card>
    );
  if (q.isError)
    return (
      <Card title={title}>
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      </Card>
    );
  if (!data?.latest) {
    return (
      <Card title={title} sub="from Testing records">
        <EmptyState>
          {t("No {test} test has been recorded at this site. Record one under", { test: t(title).toLowerCase() })}{" "}
          <Link to="/testing-records">{t("Testing records")}</Link>{" "}
          {t("and it appears here, compared with the test before it.")}
        </EmptyState>
      </Card>
    );
  }

  const latest = data.latest;
  const cmp = data.comparison;
  const levels: Level[] = data.thresholdsUsed.flatMap((th) => [
    ...(th.fail != null
      ? [
          {
            value: th.fail,
            label: t("{label} action {value}", { label: th.label, value: th.fail }),
            tone: "fail" as const,
          },
        ]
      : []),
    ...(th.attention != null
      ? [
          {
            value: th.attention,
            label: t("{label} attention {value}", { label: th.label, value: th.attention }),
            tone: "attention" as const,
          },
        ]
      : []),
  ]);
  const proposed = proposedThresholds(data.thresholdsUsed);
  const count = (o: Outcome) => latest.counts[o] ?? 0;
  const worst = [...rows].sort((a, b) => a.latest - b.latest).slice(0, reporting ? 25 : 10);

  const groups = new Map<string, { n: number; fail: number; attention: number; sum: number; prior: number[] }>();
  for (const r of rows) {
    const k = label(r.attrs[group]);
    const g = groups.get(k) ?? { n: 0, fail: 0, attention: 0, sum: 0, prior: [] };
    g.n += 1;
    g.sum += r.latest;
    if (r.latestOutcome === "fail") g.fail += 1;
    if (r.latestOutcome === "attention") g.attention += 1;
    if (r.prior != null) g.prior.push(r.prior);
    groups.set(k, g);
  }

  return (
    <Card
      title={title}
      sub={`${latest.reference} · ${formatDate(latest.performedOn)}${latest.performedBy ? ` · ${latest.performedBy}` : ""}`}
      actions={!reporting && <Link to={`/testing-records/${latest.id}`}>{t("Open the record →")}</Link>}
    >
      <div className={s.panelHead}>
        <OutcomeText outcome={latest.outcome} />
        {proposed && (
          <span
            className={`${s.badge} ${s.proposed}`}
            title={t("Judged against thresholds not yet confirmed against the certification basis")}
          >
            {t("PROPOSED THRESHOLDS")}
          </span>
        )}
        {latest.demo && <span className={`${s.badge} ${s.demo}`}>{t("DEMONSTRATION DATA")}</span>}
      </div>
      <div className={s.tiles}>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Tested")}</span>
          <span className={s.tileValue}>{latest.results}</span>
          <span className={s.tileNote}>
            {cmp
              ? t("{n} also in {reference}", { n: cmp.matched, reference: cmp.prior.reference })
              : t("no earlier test to compare")}
          </span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Action needed")}</span>
          <span className={`${s.tileValue} ${count("fail") ? s.fail : ""}`}>{count("fail")}</span>
          <span className={s.tileNote}>
            {cmp ? t("was {value}", { value: cmp.prior.counts.fail ?? 0 }) : t("below the action level")}
          </span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Needs attention")}</span>
          <span className={`${s.tileValue} ${count("attention") ? s.attention : ""}`}>{count("attention")}</span>
          <span className={s.tileNote}>
            {cmp ? t("was {value}", { value: cmp.prior.counts.attention ?? 0 }) : t("below the attention level")}
          </span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Mean {unit}", { unit })}</span>
          <span className={s.tileValue}>{fmt(cmp?.meanLatest)}</span>
          <span className={s.tileNote}>
            {cmp?.meanPrior != null
              ? t("was {value} on the same {n}", { value: fmt(cmp.meanPrior), n: cmp.matched })
              : "—"}
          </span>
        </div>
        {cmp && (
          <div className={s.tile}>
            <span className={s.tileLabel}>{t("Moved down a band")}</span>
            <span className={`${s.tileValue} ${cmp.worsened ? s.attention : ""}`}>{cmp.worsened}</span>
            <span className={s.tileNote}>
              {t("{n} moved up since {date}", { n: cmp.improved, date: formatDate(cmp.prior.performedOn) })}
            </span>
          </div>
        )}
      </div>

      <div className={s.split}>
        <DistributionChart
          latest={rows.map((r) => r.latest)}
          prior={rows.filter((r) => r.prior != null).map((r) => r.prior!)}
          levels={levels}
          unit={unit}
          latestLabel={`${latest.reference} (${latest.performedOn})`}
          priorLabel={cmp ? `${cmp.prior.reference} (${cmp.prior.performedOn})` : undefined}
        />
        <div>
          {groupBy.length > 1 && !reporting && (
            <div style={{ marginBottom: 8 }}>
              <Segmented<string>
                ariaLabel="Group by"
                value={group}
                options={groupBy.map((g) => ({ value: g.key, label: g.label }))}
                onChange={setGroup}
              />
            </div>
          )}
          <Table>
            <thead>
              <tr>
                <th>{t(groupBy.find((g) => g.key === group)?.label ?? "Group")}</th>
                <th style={{ textAlign: "end" }}>{t("Tested")}</th>
                <th style={{ textAlign: "end" }}>{t("Mean")}</th>
                <th style={{ textAlign: "end" }}>{t("Was")}</th>
                <th style={{ textAlign: "end" }}>{t("Action")}</th>
                <th style={{ textAlign: "end" }}>{t("Attention")}</th>
              </tr>
            </thead>
            <tbody>
              {[...groups]
                .sort(([, a], [, b]) => b.fail - a.fail || b.attention - a.attention || b.n - a.n)
                .map(([k, g]) => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td className={numClass}>{g.n}</td>
                    <td className={numClass}>{fmt(g.sum / g.n)}</td>
                    <td className={numClass}>
                      {g.prior.length ? fmt(g.prior.reduce((a, b) => a + b, 0) / g.prior.length) : "—"}
                    </td>
                    <td className={`${numClass} ${g.fail ? s.fail : ""}`}>{g.fail}</td>
                    <td className={`${numClass} ${g.attention ? s.attention : ""}`}>{g.attention}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>

      <h3 style={{ fontSize: 14, margin: "14px 0 6px" }}>{t("Lowest {n} in this test", { n: reporting ? 25 : 10 })}</h3>
      <Table>
        <thead>
          <tr>
            <th>{t("Asset")}</th>
            <th>{groupBy[0] ? t(groupBy[0].label) : ""}</th>
            <th style={{ textAlign: "end" }}>{t("Now ({unit})", { unit })}</th>
            <th style={{ textAlign: "end" }}>{t("Before")}</th>
            <th style={{ textAlign: "end" }}>{t("Change")}</th>
            <th>{t("Verdict")}</th>
          </tr>
        </thead>
        <tbody>
          {worst.map((r) => (
            <tr key={r.key}>
              <td className="mono">{r.assetTag ?? r.location}</td>
              <td>{label(r.attrs[groupBy[0]?.key ?? ""])}</td>
              <td className={numClass}>{fmt(r.latest)}</td>
              <td className={numClass}>{fmt(r.prior)}</td>
              <td className={numClass}>{r.delta == null ? "—" : `${r.delta > 0 ? "+" : ""}${fmt(r.delta)}`}</td>
              <td>
                <OutcomeText outcome={r.latestOutcome} />
                {r.priorOutcome && r.priorOutcome !== r.latestOutcome && (
                  <span className={s.tileNote}>
                    {" "}
                    · {t("was {value}", { value: t(OUTCOME_LABEL[r.priorOutcome]).toLowerCase() })}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className={s.note}>{latest.outcomeBasis}</p>
    </Card>
  );
}

const CONSUMPTION = [
  { key: "energy_register_kwh", title: "Electricity", unit: "kWh" },
  { key: "water_register_m3", title: "Water", unit: "m³" },
  { key: "generation_register_kwh", title: "On-site generation", unit: "kWh" },
];

/**
 * Consumption from the meters' own registers: the daily total with its
 * seven-day average as the trend, and each meter's last 30 days against the
 * 30 before — the series the predictive and analytics screens reason over.
 */
export function ConsumptionCard({ assetTypes, reporting }: { assetTypes: string[]; reporting: boolean }) {
  const [days, setDays] = useState<"30" | "90" | "180">("90");
  const q = useConsumption(assetTypes, "day", Number(days) + 30);
  const [measure, setMeasure] = useState(CONSUMPTION[0].key);
  const end = useMemo(() => new Date(), []);

  if (q.isPending)
    return (
      <Card title="Consumption and trend">
        <Skeleton rows={5} />
      </Card>
    );
  if (q.isError)
    return (
      <Card title="Consumption and trend">
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      </Card>
    );
  const points = q.data;
  const available = CONSUMPTION.filter((c) => points.some((p) => p.measurement === c.key));
  if (available.length === 0) {
    return (
      <Card title="Consumption and trend">
        <EmptyState>
          No meter readings in this period, so there's no usage to chart.
        </EmptyState>
      </Card>
    );
  }
  const current = available.find((c) => c.key === measure) ?? available[0];
  const cutoff = new Date(end.getTime() - Number(days) * 86_400_000).toISOString().slice(0, 10);
  const series = totalSeries(points, current.key).filter((p) => p.period > cutoff);
  const meters = compareWindows(points, current.key, end, 30);
  const total = series.reduce((a, p) => a + p.value, 0);
  const firstAvg = series.find((p) => p.average != null)?.average ?? null;
  const lastAvg = [...series].reverse().find((p) => p.average != null)?.average ?? null;
  const trend = firstAvg && lastAvg ? (100 * (lastAvg - firstAvg)) / firstAvg : null;
  const resets = points.filter((p) => p.measurement === current.key).reduce((a, p) => a + p.resets, 0);

  return (
    <Card
      title="Consumption and trend"
      sub="Usage between meter readings; gaps aren't counted as zero"
    >
      {!reporting && (
        <div className={s.panelHead}>
          <Segmented<string>
            ariaLabel="Measure"
            value={current.key}
            options={available.map((c) => ({ value: c.key, label: c.title }))}
            onChange={setMeasure}
          />
          <Segmented<"30" | "90" | "180">
            ariaLabel="Period"
            value={days}
            options={[
              { value: "30", label: "30 days" },
              { value: "90", label: "90 days" },
              { value: "180", label: "180 days" },
            ]}
            onChange={setDays}
          />
        </div>
      )}
      <div className={s.tiles}>
        <div className={s.tile}>
          <span className={s.tileLabel}>
            {t("{measure}, {n} days with readings", { measure: t(current.title), n: series.length })}
          </span>
          <span className={s.tileValue}>{fmt(total, 0)}</span>
          <span className={s.tileNote}>
            {t("{unit} since {date}", { unit: current.unit, date: series[0]?.period ?? "—" })}
          </span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Average per day")}</span>
          <span className={s.tileValue}>{fmt(series.length ? total / series.length : undefined, 0)}</span>
          <span className={s.tileNote}>{current.unit}</span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Trend over the period")}</span>
          <span className={s.tileValue}>{trend == null ? "—" : `${trend > 0 ? "+" : ""}${trend.toFixed(1)}%`}</span>
          <span className={s.tileNote}>{t("7-day average, first against last")}</span>
        </div>
        <div className={s.tile}>
          <span className={s.tileLabel}>{t("Register resets")}</span>
          <span className={s.tileValue}>{resets}</span>
          <span className={s.tileNote}>
            {resets ? t("counted as no consumption, not negative") : t("none in the period")}
          </span>
        </div>
      </div>
      <div className={s.split}>
        <TrendChart points={series} unit={current.unit} averageLabel={t("7-day average")} />
        <div>
          <h3 style={{ fontSize: 14, margin: "0 0 8px" }}>
            {t("Each meter: last 30 days against the 30 before (per day with readings)")}
          </h3>
          <ChangeChart
            rows={meters.map((m) => ({
              label: m.label,
              change: m.change,
              detail: t(
                "{meter}: {recent} {unit} over {recentDays} days, against {previous} over {previousDays} days",
                {
                  meter: m.assetTag,
                  recent: fmt(m.recent ?? undefined, 0),
                  unit: m.unit ?? "",
                  recentDays: m.recentDays,
                  previous: fmt(m.previous ?? undefined, 0),
                  previousDays: m.previousDays,
                },
              ),
            }))}
          />
          <Table>
            <thead>
              <tr>
                <th>{t("Meter")}</th>
                <th style={{ textAlign: "end" }}>{t("Last 30 d")}</th>
                <th style={{ textAlign: "end" }}>{t("30 d before")}</th>
                <th style={{ textAlign: "end" }}>{t("Change / day")}</th>
              </tr>
            </thead>
            <tbody>
              {meters.map((m) => (
                <tr key={m.assetTag}>
                  <td>{m.label}</td>
                  <td className={numClass}>
                    {m.recent == null ? "—" : t("{value} ({n} d)", { value: fmt(m.recent, 0), n: m.recentDays })}
                  </td>
                  <td className={numClass}>
                    {m.previous == null ? "—" : t("{value} ({n} d)", { value: fmt(m.previous, 0), n: m.previousDays })}
                  </td>
                  <td className={numClass}>
                    {m.change == null ? "—" : `${m.change > 0 ? "+" : ""}${m.change.toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  );
}
